import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { updateBusiness } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { business, userProfile, signOut, refreshBusiness } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(null);

  // Business profile state
  const [businessName, setBusinessName] = useState('');
  const [location, setLocation] = useState('');
  const [gstin, setGstin] = useState('');

  // Fraud settings state
  const [maxDailyTrips, setMaxDailyTrips] = useState(10);
  const [maxSingleAmount, setMaxSingleAmount] = useState(50000);
  const [duplicateWindow, setDuplicateWindow] = useState(30);

  // Business rules state
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Digest settings state
  const [dailyDigest, setDailyDigest] = useState(false);
  const [weeklyDigest, setWeeklyDigest] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);

  useEffect(() => {
    if (business) {
      setBusinessName(business.name || '');
      setLocation(business.location || '');
      setGstin(business.gstin || '');
      setMaxDailyTrips(business.fraud_settings?.max_daily_trips || 10);
      setMaxSingleAmount(business.fraud_settings?.max_single_amount || 50000);
      setDuplicateWindow(business.fraud_settings?.duplicate_window_minutes || 30);
      // Digest settings
      setDailyDigest(business.digest_settings?.daily_enabled || false);
      setWeeklyDigest(business.digest_settings?.weekly_enabled || false);
    }
  }, [business]);

  const fetchRules = useCallback(async (includeInactive = false) => {
    if (!business?.id) return;
    setRulesLoading(true);
    try {
      const resp = await fetch(
        `${API}/api/businesses/${business.id}/rules?include_inactive=${includeInactive}`
      );
      if (resp.ok) {
        const data = await resp.json();
        setRules(data.rules || []);
      }
    } catch (err) {
      console.error('Fetch rules error:', err);
    } finally {
      setRulesLoading(false);
    }
  }, [business?.id]);

  useEffect(() => {
    fetchRules(showHistory);
  }, [fetchRules, showHistory]);

  const handleDeactivateRule = async (ruleId) => {
    if (!business?.id) return;
    try {
      const resp = await fetch(
        `${API}/api/businesses/${business.id}/rules/${ruleId}`,
        { method: 'DELETE' }
      );
      if (resp.ok) {
        toast.success('Rule deactivated');
        fetchRules(showHistory);
      }
    } catch (err) {
      console.error('Deactivate rule error:', err);
      toast.error('Failed to deactivate rule');
    }
  };

  const save = async (section, payload) => {
    if (!business?.id) return;
    setSaving(section);
    try {
      await updateBusiness(business.id, payload);
      await refreshBusiness();
      toast.success('Saved successfully');
    } catch (err) {
      console.error(`Save ${section} error:`, err);
      toast.error('Could not save. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveProfile = () => save('profile', { name: businessName, location, gstin });
  const handleSaveFraud = () => save('fraud', { fraud_settings: { max_daily_trips: maxDailyTrips, max_single_amount: maxSingleAmount, duplicate_window_minutes: duplicateWindow } });

  const handleSaveDigest = () => save('digest', {
    digest_settings: { daily_enabled: dailyDigest, weekly_enabled: weeklyDigest, send_time: '08:00', timezone: 'Asia/Kolkata' }
  });

  const handleSendDigestNow = async (period) => {
    if (!business?.id) return;
    setDigestLoading(true);
    try {
      const resp = await fetch(`${API}/api/digest/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ business_id: business.id, period }),
      });
      const data = await resp.json();
      if (resp.ok && data.sent_to?.length > 0) {
        toast.success(`${period === 'daily' ? 'Daily' : 'Weekly'} digest sent!`);
      } else if (data.already_sent > 0) {
        toast.info('Digest already sent today');
      } else {
        toast.error('Could not send digest. Check WhatsApp number.');
      }
    } catch (err) {
      console.error('Send digest error:', err);
      toast.error('Send failed');
    } finally {
      setDigestLoading(false);
    }
  };

  const handleLogout = async () => { await signOut(); navigate('/'); };

  const SaveBtn = ({ section }) => (
    <Button size="sm"
      onClick={section === 'profile' ? handleSaveProfile : section === 'fraud' ? handleSaveFraud : handleSaveDigest}
      disabled={saving === section}
      className="text-white border-0"
      style={{ background: 'rgba(10,132,255,.2)', border: '1px solid rgba(10,132,255,.4)' }}
      data-testid={`save-${section}-btn`}>
      {saving === section ? (
        <svg className="animate-spin w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
        </svg>
      ) : (
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )}
      {saving === section ? 'Saving...' : 'Save'}
    </Button>
  );

  const _SCOPE_COLORS = {
    person: { bg: 'rgba(48,209,88,.12)', color: '#30D158' },
    item:   { bg: 'rgba(10,132,255,.12)', color: '#0A84FF' },
    party:  { bg: 'rgba(255,159,10,.12)', color: '#FF9F0A' },
    global: { bg: 'rgba(142,142,147,.12)', color: '#8E8E93' },
  };

  // Compute rule change history groups — only rules with >1 version
  const historyGroups = useMemo(() => {
    if (!showHistory || rules.length === 0) return [];
    const groups = {};
    rules.forEach(r => {
      const key = r.rule_name;
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    });
    return Object.entries(groups)
      .filter(([, versions]) => versions.length > 1)
      .map(([name, versions]) => ({
        name,
        // Sort: active first, then by created_at descending
        versions: [...versions].sort((a, b) => {
          if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
          return new Date(b.created_at) - new Date(a.created_at);
        }),
      }));
  }, [rules, showHistory]);

  return (
    <div className="min-h-screen text-white" data-testid="settings-page" style={{ background: '#000', fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif" }}>
      {/* Header */}
      <header className="p-4 sticky top-0 z-10" style={{ background: 'rgba(0,0,0,.85)', backdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid #1C1C1E' }}>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Settings</h1>
            <p className="text-sm" style={{ color: '#8E8E93' }}>Business Configuration</p>
          </div>
        </div>
      </header>

      <ScrollArea className="h-[calc(100vh-80px)]">
        <main className="p-4 space-y-4 pb-20">
          {/* User Profile (read-only) */}
          <Card className="border-0" style={{ background: '#1C1C1E', border: '1px solid #2C2C2E' }}>
            <CardHeader>
              <CardTitle className="text-white">Profile</CardTitle>
              <CardDescription style={{ color: '#8E8E93' }}>Your account details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,45,85,.15)' }}>
                  <span className="text-2xl" style={{ color: '#FF2D55' }}>{userProfile?.name?.[0]?.toUpperCase() || '?'}</span>
                </div>
                <div>
                  <p className="font-medium text-white">{userProfile?.name || 'Unknown'}</p>
                  <p className="text-sm" style={{ color: '#8E8E93' }}>{userProfile?.phone}</p>
                  <p className="text-xs capitalize" style={{ color: '#30D158' }}>{userProfile?.role || 'user'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Business Profile */}
          <Card className="border-0" style={{ background: '#1C1C1E', border: '1px solid #2C2C2E' }}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Business Profile</CardTitle>
                  <CardDescription style={{ color: '#8E8E93' }}>Business information</CardDescription>
                </div>
                <SaveBtn section="profile" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label style={{ color: '#8E8E93' }}>Business Name</Label>
                <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                  className="border-0 text-white placeholder:text-[#48484A]"
                  style={{ background: '#2C2C2E' }} data-testid="business-name-input" />
              </div>
              <div className="space-y-2">
                <Label style={{ color: '#8E8E93' }}>Location</Label>
                <Input value={location} onChange={(e) => setLocation(e.target.value)}
                  className="border-0 text-white placeholder:text-[#48484A]"
                  style={{ background: '#2C2C2E' }} data-testid="location-input" />
              </div>
              <div className="space-y-2">
                <Label style={{ color: '#8E8E93' }}>GSTIN (Optional)</Label>
                <Input value={gstin} onChange={(e) => setGstin(e.target.value)} placeholder="Not registered"
                  className="border-0 text-white placeholder:text-[#48484A]"
                  style={{ background: '#2C2C2E' }} data-testid="gstin-input" />
              </div>
            </CardContent>
          </Card>

          {/* Business Rules (read-only, managed via WhatsApp) */}
          <Card className="border-0" style={{ background: '#1C1C1E', border: '1px solid #2C2C2E' }} data-testid="business-rules-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Business Rules</CardTitle>
                  <CardDescription style={{ color: '#8E8E93' }}>
                    Set via WhatsApp · Auto-applied to all calculations
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowHistory(h => !h)}
                    className="text-xs px-3 py-1 rounded-full transition-colors"
                    style={{
                      background: showHistory ? 'rgba(10,132,255,.2)' : 'rgba(44,44,46,1)',
                      color: showHistory ? '#0A84FF' : '#8E8E93',
                      border: '1px solid',
                      borderColor: showHistory ? 'rgba(10,132,255,.4)' : '#3A3A3C',
                    }}
                    data-testid="rules-history-toggle">
                    {showHistory ? 'Active Only' : 'Show History'}
                  </button>
                  <button onClick={() => fetchRules(showHistory)} title="Refresh"
                    style={{ color: '#8E8E93' }}>
                    <svg className={`w-4 h-4 ${rulesLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {rulesLoading ? (
                <div className="flex items-center justify-center py-8" style={{ color: '#8E8E93' }}>
                  <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Loading rules...
                </div>
              ) : rules.length === 0 ? (
                <div className="text-center py-8" data-testid="rules-empty-state">
                  <p className="text-sm" style={{ color: '#636366' }}>No rules yet</p>
                  <p className="text-xs mt-1" style={{ color: '#48484A' }}>
                    Send a WhatsApp message like "cement rate 380 per bag" to set one
                  </p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="rules-list">
                  {rules.map((rule, idx) => {
                    const scopeStyle = _SCOPE_COLORS[rule.applies_to_type] || _SCOPE_COLORS.global;
                    const isInactive = !rule.is_active;
                    return (
                      <div key={rule.id || idx}
                        className="p-3 rounded-xl flex items-start justify-between gap-2"
                        style={{
                          background: isInactive ? 'rgba(44,44,46,.4)' : '#2C2C2E',
                          opacity: isInactive ? 0.6 : 1,
                        }}
                        data-testid={`rule-item-${idx}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-white truncate">{rule.rule_name}</p>
                            <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                              style={{ background: scopeStyle.bg, color: scopeStyle.color }}>
                              {rule.applies_to_name || rule.applies_to_type}
                            </span>
                            {isInactive && (
                              <span className="text-xs px-2 py-0.5 rounded-full"
                                style={{ background: 'rgba(255,69,58,.12)', color: '#FF453A' }}>
                                Inactive
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            {rule.value != null && (
                              <span className="text-sm font-medium" style={{ color: '#30D158' }}>
                                ₹{Number(rule.value).toLocaleString('en-IN')}
                                {rule.unit ? `/${rule.unit}` : ''}
                              </span>
                            )}
                            {rule.effective_from && (
                              <span className="text-xs" style={{ color: '#8E8E93' }}>
                                from {new Date(rule.effective_from).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                              </span>
                            )}
                          </div>
                          {rule.source_message && (
                            <p className="text-xs mt-1 truncate" style={{ color: '#636366' }}>
                              "{rule.source_message}"
                            </p>
                          )}
                        </div>
                        {!isInactive && (
                          <button
                            onClick={() => handleDeactivateRule(rule.id)}
                            className="flex-shrink-0 text-xs px-2 py-1 rounded-lg transition-colors"
                            style={{ color: '#FF453A', background: 'rgba(255,69,58,.12)' }}
                            data-testid={`deactivate-rule-${idx}`}>
                            Deactivate
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-4 p-3 rounded-xl" style={{ background: 'rgba(10,132,255,.08)', border: '1px solid rgba(10,132,255,.2)' }}>
                <p className="text-xs" style={{ color: '#0A84FF' }}>
                  Rules are set by sending natural language messages on WhatsApp.
                  Example: <em>"Ramesh commission 5%"</em> or <em>"cement rate 380 per bag"</em>
                </p>
              </div>

              {/* Rule Change History Timeline */}
              {showHistory && historyGroups.length > 0 && (
                <div className="mt-5" data-testid="rule-change-history">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-4 rounded-full" style={{ background: '#FF9F0A' }} />
                    <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#8E8E93' }}>
                      Change History
                    </p>
                  </div>
                  <div className="space-y-4">
                    {historyGroups.map(({ name, versions }) => (
                      <div key={name} className="p-3 rounded-xl" style={{ background: '#2C2C2E' }}
                        data-testid={`history-group-${name.replace(/\s+/g, '-').toLowerCase()}`}>
                        <p className="text-xs font-semibold mb-3" style={{ color: '#EBEBF5CC' }}>{name}</p>
                        <div className="relative">
                          {/* Vertical connector line */}
                          <div className="absolute left-[7px] top-3 bottom-3 w-px" style={{ background: 'rgba(60,60,67,.6)' }} />
                          <div className="space-y-3">
                            {versions.map((v, vi) => {
                              const isFirst = vi === 0;
                              const dotColor = v.is_active ? '#30D158' : '#FF9F0A';
                              const valueStr = v.value != null
                                ? `₹${Number(v.value).toLocaleString('en-IN')}${v.unit ? `/${v.unit}` : ''}`
                                : '—';
                              const dateStr = v.created_at
                                ? new Date(v.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                : '—';
                              return (
                                <div key={v.id || vi} className="flex items-start gap-3 pl-1">
                                  <div className="flex-shrink-0 mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center"
                                    style={{ background: isFirst ? dotColor : 'transparent', borderColor: dotColor }}>
                                    {isFirst && <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#000' }} />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="text-sm font-medium" style={{ color: v.is_active ? '#30D158' : '#FF9F0A' }}>
                                        {valueStr}
                                      </span>
                                      <span className="text-xs flex-shrink-0" style={{ color: '#636366' }}>{dateStr}</span>
                                    </div>
                                    {v.applies_to_name && (
                                      <p className="text-xs mt-0.5" style={{ color: '#8E8E93' }}>
                                        {v.applies_to_type}: {v.applies_to_name}
                                      </p>
                                    )}
                                    <span className="inline-block text-xs mt-1 px-1.5 py-0.5 rounded"
                                      style={{
                                        background: v.is_active ? 'rgba(48,209,88,.12)' : 'rgba(255,159,10,.12)',
                                        color: v.is_active ? '#30D158' : '#FF9F0A',
                                      }}>
                                      {v.is_active ? 'Current' : 'Superseded'}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {showHistory && historyGroups.length === 0 && rules.length > 0 && (
                <p className="text-xs mt-3 text-center" style={{ color: '#48484A' }}
                  data-testid="no-history-message">
                  No rule changes yet — all rules are on their first version
                </p>
              )}
            </CardContent>
          </Card>

          {/* WhatsApp Digest */}
          <Card className="border-0" style={{ background: '#1C1C1E', border: '1px solid #2C2C2E' }} data-testid="digest-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">WhatsApp Digest</CardTitle>
                  <CardDescription style={{ color: '#8E8E93' }}>
                    Auto-summary sent at 8 AM IST
                  </CardDescription>
                </div>
                <SaveBtn section="digest" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Daily toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: '#2C2C2E' }}>
                <div>
                  <p className="text-sm font-medium text-white">Daily Summary</p>
                  <p className="text-xs mt-0.5" style={{ color: '#8E8E93' }}>Today's entries every morning</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setDailyDigest(v => !v)}
                    data-testid="daily-digest-toggle"
                    className="relative w-11 h-6 rounded-full transition-colors"
                    style={{ background: dailyDigest ? '#30D158' : '#3A3A3C' }}>
                    <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform"
                      style={{ transform: dailyDigest ? 'translateX(20px)' : 'translateX(0)' }} />
                  </button>
                  {dailyDigest && (
                    <button
                      onClick={() => handleSendDigestNow('daily')}
                      disabled={digestLoading}
                      className="text-xs px-2 py-1 rounded-lg transition-colors"
                      style={{ color: '#0A84FF', background: 'rgba(10,132,255,.12)' }}
                      data-testid="send-daily-now-btn">
                      {digestLoading ? '...' : 'Send Now'}
                    </button>
                  )}
                </div>
              </div>

              {/* Weekly toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: '#2C2C2E' }}>
                <div>
                  <p className="text-sm font-medium text-white">Weekly Summary</p>
                  <p className="text-xs mt-0.5" style={{ color: '#8E8E93' }}>Last 7 days, every Monday morning</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setWeeklyDigest(v => !v)}
                    data-testid="weekly-digest-toggle"
                    className="relative w-11 h-6 rounded-full transition-colors"
                    style={{ background: weeklyDigest ? '#30D158' : '#3A3A3C' }}>
                    <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform"
                      style={{ transform: weeklyDigest ? 'translateX(20px)' : 'translateX(0)' }} />
                  </button>
                  {weeklyDigest && (
                    <button
                      onClick={() => handleSendDigestNow('weekly')}
                      disabled={digestLoading}
                      className="text-xs px-2 py-1 rounded-lg transition-colors"
                      style={{ color: '#0A84FF', background: 'rgba(10,132,255,.12)' }}
                      data-testid="send-weekly-now-btn">
                      {digestLoading ? '...' : 'Send Now'}
                    </button>
                  )}
                </div>
              </div>

              <div className="p-3 rounded-xl" style={{ background: 'rgba(48,209,88,.08)', border: '1px solid rgba(48,209,88,.2)' }}>
                <p className="text-xs" style={{ color: '#30D158' }}>
                  Digest goes to all business owners at 8 AM IST. Toggle on and save to activate.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Anomaly Thresholds */}
          <Card className="border-0" style={{ background: '#1C1C1E', border: '1px solid #2C2C2E' }}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Anomaly Thresholds</CardTitle>
                  <CardDescription style={{ color: '#8E8E93' }}>Alert triggers for unusual activity</CardDescription>
                </div>
                <SaveBtn section="fraud" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label style={{ color: '#8E8E93' }}>Max Daily Trips</Label>
                <Input type="number" value={maxDailyTrips} onChange={(e) => setMaxDailyTrips(Number(e.target.value))}
                  className="border-0 text-white" style={{ background: '#2C2C2E' }} data-testid="max-trips-input" />
                <p className="text-xs" style={{ color: '#636366' }}>Alert if employee logs more trips in a day</p>
              </div>
              <div className="space-y-2">
                <Label style={{ color: '#8E8E93' }}>Max Single Amount</Label>
                <div className="flex items-center gap-2">
                  <span style={{ color: '#8E8E93' }}>₹</span>
                  <Input type="number" value={maxSingleAmount} onChange={(e) => setMaxSingleAmount(Number(e.target.value))}
                    className="border-0 text-white" style={{ background: '#2C2C2E' }} data-testid="max-amount-input" />
                </div>
                <p className="text-xs" style={{ color: '#636366' }}>Alert for transactions above this amount</p>
              </div>
              <div className="space-y-2">
                <Label style={{ color: '#8E8E93' }}>Duplicate Window (minutes)</Label>
                <Input type="number" value={duplicateWindow} onChange={(e) => setDuplicateWindow(Number(e.target.value))}
                  className="border-0 text-white" style={{ background: '#2C2C2E' }} data-testid="duplicate-window-input" />
                <p className="text-xs" style={{ color: '#636366' }}>Detect duplicates within this time window</p>
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-0" style={{ background: 'rgba(255,69,58,.08)', border: '1px solid rgba(255,69,58,.25)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#FF453A' }}>Account</CardTitle>
            </CardHeader>
            <CardContent>
              <Button variant="destructive" className="w-full border-0"
                style={{ background: 'rgba(255,69,58,.15)', color: '#FF453A', border: '1px solid rgba(255,69,58,.3)' }}
                onClick={handleLogout} data-testid="logout-button">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </Button>
            </CardContent>
          </Card>

          <div className="text-center py-4">
            <p className="text-sm" style={{ color: '#48484A' }}>Noteho v1.0 (Phase 2)</p>
            <p className="text-xs mt-1" style={{ color: '#38383A' }}>WhatsApp-first AI Business Management</p>
          </div>
        </main>
      </ScrollArea>
    </div>
  );
};

export default SettingsPage;
